import { useState, useEffect, useCallback } from 'react';
import { collection, onSnapshot, query, orderBy, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

const READ_KEY = 'hc_read_announcements';

function loadReadIds() {
  try {
    const saved = localStorage.getItem(READ_KEY);
    if (saved) {
      const ids = JSON.parse(saved);
      if (Array.isArray(ids)) return new Set(ids);
    }
  } catch { /* ignore */ }
  return new Set();
}

function saveReadIds(ids) {
  try {
    localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
  } catch (err) {
    console.warn('[useAnnouncements] failed to persist read state:', err);
  }
}

export function useAnnouncements(user) {
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [readIds, setReadIds] = useState(loadReadIds);

  useEffect(() => {
    const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
    return onSnapshot(q, (snap) => {
      setAnnouncements(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      setLoading(false);
    }, () => setLoading(false));
  }, []);

  const unreadCount = announcements.filter((a) => !readIds.has(a.id)).length;

  const markRead = useCallback((id) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      saveReadIds(next);
      return next;
    });
  }, []);

  const markAllRead = useCallback(() => {
    setReadIds((prev) => {
      const next = new Set(prev);
      announcements.forEach((a) => next.add(a.id));
      saveReadIds(next);
      return next;
    });
  }, [announcements]);

  const postAnnouncement = useCallback(async (title, body, priority) => {
    if (!user) return;
    await addDoc(collection(db, 'announcements'), {
      title: title.trim(),
      body: body.trim(),
      priority: priority || 'normal',
      authorName: user.displayName || user.email || 'Admin',
      authorUid: user.uid,
      createdAt: serverTimestamp(),
    });
  }, [user]);

  const deleteAnnouncement = useCallback(async (id) => {
    await deleteDoc(doc(db, 'announcements', id));
  }, []);

  return { announcements, loading, unreadCount, readIds, markRead, markAllRead, postAnnouncement, deleteAnnouncement };
}
